import { Field } from "@ui/components/Field";
import { resolveValidationState } from "@ui/components/shared/resolveValidationState";
import { TextField, TextFieldInput, type TextFieldProps } from "@ui/primitives/TextField";
import type { ReactNode } from "react";

export interface StandardTextFieldProps extends TextFieldProps {
  label: string;
  /** Whether the field is optional — appends a styled "(optional)" hint to the label */
  isOptional?: boolean;
  /** Visually hides the label (kept for screen readers) — for search or inline fields. */
  isLabelHidden?: boolean;
  leadIcon?: ReactNode;
  placeholder?: string;
  description?: ReactNode;
  errorMessage?: ReactNode;
  successMessage?: ReactNode;
  /**
   * Styles the field's outer box — this is the element a parent flex/grid lays out,
   * so `flex-1`, `w-*` and `min-w-0` belong here. String only: the RAC render-prop
   * form has nothing to style, since the RAC root is `display: contents`.
   */
  className?: string;
}

const inputClassName =
  "flex-1 bg-transparent caret-blue-400 text-base/6 text-neutral-900 outline-none placeholder:text-neutral-400";

export function StandardTextField({
  label,
  isOptional,
  isLabelHidden,
  leadIcon,
  placeholder,
  description,
  errorMessage,
  successMessage,
  className,
  ...props
}: StandardTextFieldProps) {
  const validationState = resolveValidationState(props.isInvalid, successMessage);
  const showErrorMessage = props.isInvalid && errorMessage;

  return (
    <Field className={className} isInvalid={props.isInvalid} isOptional={isOptional}>
      <TextField {...props} className="contents">
        <Field.Label isHidden={isLabelHidden}>{label}</Field.Label>
        <Field.Control asGroup leadIcon={leadIcon} validationState={validationState}>
          <TextFieldInput className={inputClassName} placeholder={placeholder} />
        </Field.Control>
        {showErrorMessage ? (
          <Field.Error>{errorMessage}</Field.Error>
        ) : successMessage ? (
          <Field.Success>{successMessage}</Field.Success>
        ) : description ? (
          <Field.Description>{description}</Field.Description>
        ) : null}
      </TextField>
    </Field>
  );
}
